import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db6fe64be24agvuu3j40';
const MAX_MESSAGE = 500;

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Anti-spam honeypot check: if filled, silently show success without sending.
    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessage('');
      return;
    }

    formData.delete('website_alt');

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        '';

      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
        setMessage('');
      } else {
        setStatus('error');
        setFormError(
          serverMsg && !isSpam
            ? String(serverMsg)
            : '提交失败，请稍后重试或检查填写内容。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请检查网络后重试。');
    }
  };

  return (
    <form
      id="about-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/80 bg-background-50 p-6 md:p-8"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">
        留言与反馈
      </h3>
      <p className="mt-2 text-sm text-foreground-600">
        对平台有任何建议或内容合作想法，欢迎留言告诉我们。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground-700">称呼</span>
          <input
            type="text"
            name="name"
            required
            placeholder="请输入您的称呼"
            className="rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground-700">邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="用于接收回复的邮箱"
            className="rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-2">
        <span className="text-sm font-medium text-foreground-700">反馈内容</span>
        <textarea
          name="message"
          required
          maxLength={MAX_MESSAGE}
          rows={5}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="请描述您的建议或反馈（500 字以内）"
          className="resize-none rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
        <span className="self-end text-xs text-foreground-400">
          {message.length} / {MAX_MESSAGE}
        </span>
      </label>

      <label className="mv-form-guard" aria-hidden="true">
        <span>备用联系方式</span>
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </label>

      <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-foreground-500">
          提交后我们会尽快处理您的反馈，感谢您的支持。
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-50/40 border-t-background-50" />
              提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line text-lg" aria-hidden="true" />
              提交反馈
            </>
          )}
        </button>
      </div>

      {status === 'success' && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700">
          <i className="ri-checkbox-circle-line text-lg" aria-hidden="true" />
          提交成功，感谢您的留言！
        </p>
      )}
      {status === 'error' && formError && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
          <i className="ri-error-warning-line text-lg" aria-hidden="true" />
          {formError}
        </p>
      )}
    </form>
  );
}